/**
 * RFQ permissions helpers — mirror of `front-react/src/lib/rfq-permissions.ts`.
 */
import type { RFQProject } from "./types";

export type ProjectRole = "owner" | "evaluator" | "approver" | "observer" | "supplier" | "none";

// Get user's role in the project
export function getUserProjectRole(
  project: RFQProject,
  userEmail: string,
): ProjectRole {
  if (!userEmail) return "none";

  // Check if owner
  if (project.created_by_email === userEmail) {
    return "owner";
  }

  const stakeholders = project.step_04_stakeholders;
  if (!stakeholders) return "none";

  // Check evaluator
  if (stakeholders.evaluator_emails?.includes(userEmail)) {
    return "evaluator";
  }

  // Check approver
  if (stakeholders.approver_emails?.includes(userEmail)) {
    return "approver";
  }

  // Check observer
  if (stakeholders.observer_emails?.includes(userEmail)) {
    return "observer";
  }

  // Check supplier
  if (stakeholders.supplier_emails?.includes(userEmail)) {
    return "supplier";
  }

  return "none";
}

// Can edit project (owner only)
export function canEditProject(
  project: RFQProject,
  userEmail: string,
): boolean {
  return getUserProjectRole(project, userEmail) === "owner";
}

// Can approve (approver only, at specific steps)
export function canApprove(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "approver") return false;

  // Can approve at Step 6 (Internal Approval) or Step 14 (Award Decision)
  const approvableStates = ["RFQ_DRAFTED", "AWARD_DECISION_PENDING"];
  return approvableStates.includes(project.workflow_state);
}

// Can score quotes (evaluator only, at Step 12)
export function canScore(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "evaluator") return false;

  return project.workflow_state === "EVALUATION_IN_PROGRESS";
}

// Can view project (any stakeholder)
export function canViewProject(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  return role !== "none";
}

// Can submit quote (supplier only, at Step 9)
export function canSubmitQuote(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "supplier") return false;

  return project.workflow_state === "QUOTES_UNDER_REVIEW";
}

// Can negotiate (owner only, at Step 10)
export function canNegotiate(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "owner") return false;

  return project.workflow_state === "NEGOTIATION_IN_PROGRESS";
}

// Can ask questions (supplier only, during Q&A period)
export function canAskQuestion(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "supplier") return false;

  return project.workflow_state === "QNA_OPEN";
}

// Can answer questions (owner only, during Q&A period)
export function canAnswerQuestion(
  project: RFQProject,
  userEmail: string,
): boolean {
  const role = getUserProjectRole(project, userEmail);
  if (role !== "owner") return false;

  const qaStates = ["QNA_OPEN", "QNA_CLOSED"];
  return qaStates.includes(project.workflow_state);
}
